/**
 * Service list view component (table-like)
 */
import { Service } from '@/types';

interface ServiceListViewProps {
  services: Service[];
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
}

export function ServiceListView({ services, onEdit, onDelete }: ServiceListViewProps) {
  const formatPrice = (cents: number) => {
    return `€${(cents / 100).toFixed(2)}`;
  };

  const formatDuration = (minutes: number) => {
    if (minutes < 60) return `${minutes}min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      haircut: 'bg-info/10 text-info',
      beard: 'bg-accent/10 text-accent',
      shave: 'bg-success/10 text-success',
      styling: 'bg-accent/10 text-accent',
      coloring: 'bg-warning/10 text-warning',
      treatment: 'bg-success/10 text-success',
    };
    return colors[category] || 'bg-surface-2 text-ink';
  };

  return (
    <div className="bg-white shadow-md rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-hairline">
          <thead className="bg-surface-2">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-ink-subtle uppercase tracking-wider">
                Service
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-ink-subtle uppercase tracking-wider">
                Category
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-ink-subtle uppercase tracking-wider">
                Duration
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-ink-subtle uppercase tracking-wider">
                Price
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-ink-subtle uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-ink-subtle uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-hairline">
            {services.map((service) => (
              <tr key={service.serviceId} className="hover:bg-surface-3 transition-colors">
                <td className="px-6 py-4">
                  <div className="flex items-center">
                    <div className="text-2xl mr-3">✂️</div>
                    <div>
                      <div className="text-sm font-medium text-ink">{service.name}</div>
                      {service.description && (
                        <div className="text-sm text-ink-subtle truncate max-w-xs">
                          {service.description}
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getCategoryColor(service.category)}`}>
                    {service.category}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-ink">{formatDuration(service.duration)}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-ink">{formatPrice(service.price)}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    service.isActive
                      ? 'bg-success/10 text-success'
                      : 'bg-danger/10 text-danger'
                  }`}>
                    {service.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button
                    onClick={() => onEdit(service.serviceId)}
                    className="text-info hover:text-info mr-4"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => onDelete(service.serviceId)}
                    className="text-danger hover:text-danger"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
